import cn from "classnames";
import BidFieldsOrderForm from "components/admin/organizations/show/bidFieldsOrderForm";
import ExtendedPropertyForm from "components/admin/organizations/show/extendedPropertyForm";
import EditUser from "components/admin/users/editUserModal";
import BidItemChanges from "components/bid/bidItemChanges";
import ItemChangePointForm from "components/bid/bidItemChanges/addressPicker/form";
import ParallelBidChange from "components/bid/bidItemChanges/parallelBidChange";
import ForceComplete from "components/bid/form/forceComplete";
import SetBidEstimatedLeaveDate from "components/bid/form/setBidEstimatedLeaveDate";
import SetBidPointDates from "components/bid/form/setBidPointDates";
import ForwardingOrderCarChange from "components/bid/forwardingOrderCarChange";
import AcceptWaybillTitle from "components/bid/show/partials/acceptWaybillTitleModal";
import AxisLoadValuesModal from "components/bid/show/partials/axisLoadValuesModal";
import BidItemChangeVisitModal from "components/bid/show/partials/bidItemChangeVisitModal";
import ChangePointVisit from "components/bid/show/partials/changePointVisitModal";
import PlanRefueling from "components/bid/show/partials/planRefueling";
import EditPlanRefueling from "components/bid/show/partials/planRefueling/edit";
import ScenarioAnswersModal from "components/bid/show/partials/scenarioAnswersModal";
import TripScenarioAnswersModal from "components/bid/show/partials/tripScenarioAnswersModal";
import BidPointsOrderModal from "components/bid/show/partials/bidPointsOrderModal";
import CreateRecalculate from "components/calculationQueues/form/byBidModal";
import CounterpartyContractForm from "components/cargoOwners/contracts/modal";
import CreateCargoOwner from "components/cargoOwners/form/modal";
import CounterpartyPointForm from "components/cargoOwners/points/modal";
import CounterpartyGroupForm from "components/counterpartyGroups/form/modal";
import CreateCustomPoint from "components/customPoints/form/modal";
import DistributionBidSourcesForm from "components/distributionBidSources/form";
import DistributionBidCancellation from "components/distributionBids/show/partials/distributionBidCancellation";
import DriverLicenseModal from "components/drivers/driverLicenseModal";
import CreateDriver from "components/drivers/form/modal";
import CreateFuelCard from "components/fuelCards/form/modal";
import HiredCarForm from "components/hiredPark/cars/form";
import HiredDriverForm from "components/hiredPark/drivers/form";
import HiredTrailerForm from "components/hiredPark/trailers/form";
import CreateItemChangePoint from "components/itemChangePoints/form/modal";
import OrganizationCatalogItemForm from "components/organizationCatalogs/items/form";
import BookingCommentForm from "components/planning/city/bookingCommentForm";
import PlanningCommentForm from "components/planning/city/planningCommentForm";
import AddUserForm from "components/profile/body/accessControl/addUser";
import SyncSystemForm from "components/profile/body/orgProfile/syncSystemSettings/syncSystemForm";
import CreateRegionGroup from "components/regionGroups/form/modal";
import ImportRouteControlPoints from "components/routeControlPoints/importModal";
import ErrorBoundary from "components/shared/errorBoundary";
import CreateTracker from "components/trackers/form/modal";
import ImportTrackers from "components/trackers/importModal";
import CreateTrailer from "components/trailers/form/modal";
import CreateTransportColumn from "components/transportColumns/form/modal";
import CreateTruck from "components/trucks/form/modal";
import React, { Component, FC, lazy, Suspense } from "react";
import { ModalBodyProps } from "reducers/modal";
import AddSyncStatus from "./addSyncStatus";
import BidDocuments from "./bidDocuments";
import Confirm from "./confirm";
import ExcessMileage from "./excessMileage";
import ExternalDistributionBid from "./externalDistributionBid";
import FileUpload from "./fileUpload";
import InfoModal from "./infoModal";
import LinkFuelCards from "./linkFuelCards";
import ModalMap from "./modalMap";
import SimpleMapPicker from "./modalMap/simplePicker";
import Print from "./print";
import RegistrationSuccess from "./registrationSuccess";
import ResourceActivityForm from "./resourceActivityForm";
import RouteSegments from "./routeSegments";
import RouteTemplateForm from "./routeTemplateForm";
import ShiftCarMapPicker from "./shiftCarMapPicker";
import SkippedRefuelings from "./skippedRefuelings";
import SuitableDistributionBids from "./suitableDistributionBids";
import DefaultTemplate from "./templates/default";
import UpdateExtendedProp from "./updateExtendedProp";
import UpdatePosition from "./updatePosition";

type ModalItemProps = ModalBodyProps & {
    modalId: string;
    history: any;
    close: () => void;
    location: Location;
    withoutEscClose: boolean;
    mergeMapData: (params: {}) => void;
    mod: string;
    mapMode: string;
};

const SignFile = lazy(() => import("components/shared/signFileModal"));
const CheckAndSignFile = lazy(() => import("components/shared/checkAndSignFileModal"));
const SignShippingOrderTitle = lazy(() => import("components/shared/signShippingOrderTitleModal"));
const SignWaybillResponseTitle = lazy(() => import("components/shared/signWaybillResponseTitleModal"));
const GenerateWaybillChangeTitle = lazy(() => import("components/shared/generateWaybillChangeTitleModal"));

const MODALS = {
    confirm: Confirm,
    print: Print,
    linkFuelCards: LinkFuelCards,
    excessMileage: ExcessMileage,
    createDriver: CreateDriver,
    driverLicense: DriverLicenseModal,
    createFuelCard: CreateFuelCard,
    createCustomPoint: CreateCustomPoint,
    createItemChangePoint: CreateItemChangePoint,
    createRegionGroup: CreateRegionGroup,
    createCargoOwner: CreateCargoOwner,
    counterpartyContractForm: CounterpartyContractForm,
    createTrailer: CreateTrailer,
    hiredCarForm: HiredCarForm,
    hiredTrailerForm: HiredTrailerForm,
    hiredDriverForm: HiredDriverForm,
    createTransportColumn: CreateTransportColumn,
    counterpartyGroupForm: CounterpartyGroupForm,
    createTruck: CreateTruck,
    createTracker: CreateTracker,
    importTrackers: ImportTrackers,
    importRouteControlPoints: ImportRouteControlPoints,
    createRecalculate: CreateRecalculate,
    fileUpload: FileUpload,
    routeSegments: RouteSegments,
    skippedRefuelings: SkippedRefuelings,
    infoModal: InfoModal,
    forceComplete: ForceComplete,
    changePointVisit: ChangePointVisit,
    bidItemChangeVisit: BidItemChangeVisitModal,
    setBidPointDates: SetBidPointDates,
    setBidEstimatedLeaveDate: SetBidEstimatedLeaveDate,
    routeTemplateForm: RouteTemplateForm,
    updateExtendedProp: UpdateExtendedProp,
    suitableDistributionBids: SuitableDistributionBids,
    planRefueling: PlanRefueling,
    editPlanRefueling: EditPlanRefueling,
    extendedPropertyForm: ExtendedPropertyForm,
    planningCommentForm: PlanningCommentForm,
    bookingCommentForm: BookingCommentForm,
    addSyncStatus: AddSyncStatus,
    axisLoadValues: AxisLoadValuesModal,
    organizationCatalogItemForm: OrganizationCatalogItemForm,
    scenarioAnswers: ScenarioAnswersModal,
    tripScenarioAnswers: TripScenarioAnswersModal,
    bidPointsOrder: BidPointsOrderModal,
    addUser: AddUserForm,
    editUser: EditUser,
    updatePosition: UpdatePosition,
    counterpartyPointForm: CounterpartyPointForm,
    itemChangePointForm: ItemChangePointForm,
    bidItemChanges: BidItemChanges,
    distributionBidSourcesForm: DistributionBidSourcesForm,
    syncSystem: SyncSystemForm,
    externalDistributionBid: ExternalDistributionBid,
    distributionBidCancellation: DistributionBidCancellation,
    shiftCarMapPicker: ShiftCarMapPicker,
    simpleMapPicker: SimpleMapPicker,
    resourceActivityForm: ResourceActivityForm,
    bidFieldsOrderForm: BidFieldsOrderForm,
    modalMap: ModalMap,
    signFile: (props: {}) => (
        <Suspense>
            <SignFile {...props} />
        </Suspense>
    ),

    checkAndSignFile: (props: {}) => (
        <Suspense>
            <CheckAndSignFile {...props} />
        </Suspense>
    ),

    signShippingOrderTitle: (props: {}) => (
        <Suspense>
            <SignShippingOrderTitle {...props} />
        </Suspense>
    ),

    signWaybillResponseTitle: (props: {}) => (
        <Suspense>
            <SignWaybillResponseTitle {...props} />
        </Suspense>
    ),

    generateWaybillChangeTitle: (props: {}) => (
        <Suspense>
            <GenerateWaybillChangeTitle {...props} />
        </Suspense>
    ),

    acceptWaybillTitle: AcceptWaybillTitle,
    default: DefaultTemplate,
    parallelBidChange: ParallelBidChange,
    forwardingOrderCarChange: ForwardingOrderCarChange,
    bidDocuments: BidDocuments,
    registrationSuccess: RegistrationSuccess,
};

const ModalWindow = ({ close, modalProps }: { close: () => void; modalProps: ModalItemProps }) => {
    const name = (modalProps.name.charAt(0).toLowerCase() + modalProps.name.slice(1)) as keyof typeof MODALS;
    const CurrentModal = MODALS[name] as FC<{ close: () => void }>;
    return CurrentModal ? (
        <ErrorBoundary className="modal__window modal-window">
            <CurrentModal {...modalProps} close={close} />
        </ErrorBoundary>
    ) : null;
};

class ModalItem extends Component<ModalItemProps, {}> {
    unlisten: () => void;

    componentDidMount() {
        const { history, close, name, location, withoutEscClose } = this.props;
        this.unlisten = history.listen(({ pathname }: { pathname: string }) => {
            location.pathname !== pathname && name.length && close();
        });
        if (!withoutEscClose) {
            document.addEventListener("keydown", this.onEscClick);
        }
    }

    componentWillUnmount() {
        this.unlisten();
        if (!this.props.withoutEscClose) {
            document.removeEventListener("keydown", this.onEscClick);
        }
    }

    onEscClick = (evt: KeyboardEvent) => {
        evt = evt || window.event;
        if (evt.key === "Escape" || evt.key === "Esc") {
            this.close();
        }
        document.removeEventListener("keydown", this.onEscClick);
    };

    close = () => {
        const { close, onCloseAction, mergeMapData } = this.props;
        if (onCloseAction) {
            onCloseAction();
        }
        if (mergeMapData) {
            mergeMapData({ isLoading: false });
        }
        return close();
    };

    render() {
        const { name, mod, mapMode, withoutEscClose } = this.props;
        const cname = cn("modal", {
            "modal--closed": !name || mapMode === "picker",
            "modal--full": name === "print" || name === "routeSegments",
            [`modal--${mod}`]: !!mod,
        });
        return (
            <div className={cname}>
                <button
                    aria-label="Закрыть модальное окно"
                    className="modal__overlay"
                    disabled={withoutEscClose}
                    onClick={this.close}
                    type="button"
                />

                <ModalWindow close={this.close} modalProps={this.props} />
            </div>
        );
    }
}

const ModalSwitch = ({ items, ...props }: { items: ModalItemProps[] }) =>
    items.map((item) => <ModalItem {...item} {...props} key={item.modalId} />);

export default ModalSwitch;
